// @ts-strict-ignore
import { getActionDataType, getActionName, isActionOfType } from '~/dashboard/views/workflows/service/actions';
import type {
  Action, PopulatedThreshold, SchemaInstance, WorkflowProps,
} from '~/dashboard/views/workflows/store/engine/types';
import type { ChartLevels } from './components/ThresholdsCard/types';
import { thresholdSimDisableReason } from './types';
import type { ThresholdSimDisableReason } from './types';
import { MONETARY_MLA_KEYWORDS, thresholdSimDisabledMessages } from './constants';
import type { ThresholdSimulatorParams } from '~/flow/thresholdSimulator/redux/types';
import { parseMessage } from 'flow/service/util';
import isEqual from 'lodash/isEqual';
import { ActionType } from 'flow/store/engine/constants';
import dayjs from 'dayjs';


export function getModalTitleText(focusedAction: Action): string {
  if (!focusedAction) return null;
  const actionType = focusedAction.type.replaceAll('_', ' ');
  const actionName = getActionName(focusedAction);
  return actionType.concat(' ', actionName);
}

// NOTE:  previously named findClosestPercentage() - rather than finding the first matching
//        percentile for a target threshold value, we really need to find the closest
//        percentile that matches the target value based on the operator
export function findCorrectPercentileByThresholdValueAndOperator({
  breakdownMap,
  chartLevels,
  targetThreshold,
  thresholdOperator,
}) {
  if (
    !breakdownMap
    || Object.keys(chartLevels).length === 0
    || targetThreshold === null
    || targetThreshold === undefined
    || thresholdOperator === null
    || thresholdOperator === undefined
  ) {
    return null;
  }

  const level = chartLevels[targetThreshold];
  if (level === undefined) {
    return null;
  }
  switch (thresholdOperator) {
    case 'le':
      return level.maxPercentile;
    case 'lt':
      return level.minPercentile - 1;
    case 'e':
    case 'ne':
    case 'ge':
      return level.minPercentile;
    case 'gt':
      return level.maxPercentile + 1;
  }
  return level.maxPercentile;
}

export function getEvaluationPercentileByThresholdOperator({
  breakdownMap,
  chartLevels,
  sliderPercentile,
  thresholdOperator,
}: {
  breakdownMap: Map<number, number>;
  chartLevels: ChartLevels;
  sliderPercentile: number;
  thresholdOperator: string;
}) {
  if (['ge', 'gt'].includes(thresholdOperator)) {
    return 99 - sliderPercentile;
  }
  const breakdownValue = breakdownMap.get(sliderPercentile);
  const level = chartLevels[breakdownValue];
  if (['e', 'ne'].includes(thresholdOperator) && level !== undefined) {
    if (thresholdOperator === 'e') {
      return level.totalPercent;
    }
    if (thresholdOperator === 'ne') {
      return 99 - level.totalPercent;
    }
  }
  return sliderPercentile;
}

export function thresholdOperatorIsOpposite(currentThresholdOperator, newThresholdOperator) {
  if (
    !currentThresholdOperator
    || !newThresholdOperator
    || currentThresholdOperator === newThresholdOperator
  ) {
    return false;
  }
  if (
    ['gt', 'ge'].includes(currentThresholdOperator) && ['lt', 'le'].includes(newThresholdOperator)
  ) {
    return true;
  }
  if (
    ['lt', 'le'].includes(currentThresholdOperator) && ['gt', 'ge'].includes(newThresholdOperator)
  ) {
    return true;
  }
  return false;
}

// Validators
export function isThresholdSimOperator(operator: string) {
  return ['lt', 'le', 'gt', 'ge', 'e', 'ne'].includes(operator);
}

export function isThresholdSimDataType(dataType: string) {
  return ['decimal', 'integer'].includes(dataType);
}

export function getParsedThresholdOperator(threshold: PopulatedThreshold) {
  if (threshold.right.leftTagActionResult !== null) {
    return threshold.right.leftTagActionResult ? 'set' : 'not set';
  }
  return threshold.operator;
}

// todo: clean this up and move it up to services directory
export function getServiceAttribute(
  threshold: PopulatedThreshold,
  thresholdSide: 'left' | 'right',
  workflowData: Partial<SchemaInstance>,
  workflowProps: WorkflowProps,
) {
  const side = threshold[thresholdSide];
  const action = workflowData[side.actionId];

  if (isActionOfType(action, ActionType.SERVICE)) {
    const internalServiceName = action.service.internal_name;
    const serviceDetails = workflowProps.services[internalServiceName];
    return serviceDetails.attributes[side.attributeId];
  }

  return null;
}

export function getThresholdType(
  threshold: PopulatedThreshold,
  workflowData: Partial<SchemaInstance>,
  workflowProps: WorkflowProps,
) {
  const leftAction = workflowData[threshold.left.actionId];
  if (isActionOfType(leftAction, ActionType.SERVICE)) {
    const leftAttribute = getServiceAttribute(
      threshold, 'left', workflowData, workflowProps,
    );
    const normalizedLeftAttributeName = leftAttribute.display_name.toLowerCase();

    if (normalizedLeftAttributeName.includes('score')) {
      return 'Score';
    }
    if (
      MONETARY_MLA_KEYWORDS.some(keyword => normalizedLeftAttributeName.includes(keyword))
    ) {
      return '$ Amount';
    }
  }
  return 'Value';
}

export function getThresholdSimDisabledReason(
  threshold: PopulatedThreshold,
  initialThreshold: PopulatedThreshold,
  thresholdSimParams: ThresholdSimulatorParams,
) {
  if (!threshold || !initialThreshold || !thresholdSimParams) {
    return thresholdSimDisableReason.invalidThreshold;
  }

  // validations defined in order of prescendence
  const validations = [
    {
      type: thresholdSimDisableReason.invalidDataType,
      isValid: isThresholdSimDataType(getActionDataType(threshold.left)),
    },
    {
      type: thresholdSimDisableReason.lhsChanged,
      isValid: isEqual(initialThreshold.left, threshold.left),
    },
    {
      type: thresholdSimDisableReason.invalidOperator,
      isValid: isThresholdSimOperator(threshold.operator),
    },
    {
      type: thresholdSimDisableReason.invalidSave,
      // @ts-expect-error
      isValid: thresholdSimParams?.error?.status !== 404,
    },
    {
      type: thresholdSimDisableReason.pendingData,
      isValid: !thresholdSimParams.isLoading,
    },
    {
      type: thresholdSimDisableReason.insufficientEvaluations,
      isValid: !thresholdSimParams.evalsNeeded,
    },
  ];

  const violatedConditions = validations.filter(validation => !validation.isValid);

  if (!violatedConditions.length) {
    // All validations have passed. Do not return a disabled reason.
    return null;
  }

  // for now, just return 1 disabled reason at a time
  return violatedConditions[0].type;
}

export function getThresholdSimDisabledMessage(
  disabledReason: ThresholdSimDisableReason,
  simParams: ThresholdSimulatorParams,
) {
  const tokens = { '{{evalsNeeded}}': String(simParams.evalsNeeded) };
  const message = thresholdSimDisabledMessages[disabledReason];

  return message ? parseMessage(message, tokens) : 'Something went wrong.';
}

export function formatThresholdSimParams(thresholdId, queryResponse) {
  const dateFormat = 'YYYY-MM-DD';
  const { data: response, error, isLoading } = queryResponse;
  const validStartDate = response?.data?.oldest_eval_created_at && (
    dayjs.utc(response?.data?.oldest_eval_created_at).format(dateFormat)
  );
  const validEndDate = response?.data?.newest_eval_created_at && (
    dayjs.utc(response?.data?.newest_eval_created_at).format(dateFormat)
  );

  return {
    validStartDate,
    validEndDate,
    evalsNeeded: response?.data?.evals_needed,
    activeThreshold: thresholdId,
    error: error?.response,
    isLoading,
  };
}
